import React from 'react';
import SEOHead from '../components/SEOHead';
import './TermsAndConditions.css';

const TermsSellers = () => {
  return (
    <div className="terms-container">
      <SEOHead 
        title="SDeal Seller Terms & Conditions - SDeal"
        description="Terms and Conditions for Sellers on the SDeal platform"
        keywords="SDeal, seller terms, conditions, marketplace"
      />
      <div className="w3-content w3-padding-64">
        <h1>SDeal Seller Terms & Conditions</h1>
        <p><strong>Version: September 2026 – Groningen, The Netherlands</strong></p>

        <h3>Scope</h3>
        <p>These Terms and Conditions ("Terms") govern the commercial relationship between SDeal B.V., registered at Osloweg 110, 9723 BX Groningen, The Netherlands (hereinafter "SDeal"), and any merchant ("Seller") who offers products through the SDeal platform ("Platform").</p>
        <p>By signing the SDeal Agreement or registering as a Seller, you agree to be bound by these Terms.</p>
        <p>1.1 These Terms apply to all services provided by SDeal to Sellers, including but not limited to access to the Platform, SaaS functionality, marketing and visibility services, payment facilitation, and commercial agency activities.</p>
        <p>1.2 The Terms form an integral part of the SDeal Agreement. In case of conflict between the Agreement and these Terms, the Agreement prevails.</p>
        <p>1.3 SDeal reserves the right to modify these Terms at any time. Sellers will be notified at least 30 days in advance of substantial changes. Continued use of the Platform constitutes acceptance of the updated Terms.</p>
        <p>1.4 Future Changes (Pricing, Packages and Service Structure)</p>
        <p>SDeal may modify its pricing, package structure (including Packages A, B and C), commission rules, transaction fees, SaaS features, or any other commercial conditions with 30 days' prior written notice (email is sufficient).</p>
        <p>If the Seller does not agree to the amended conditions, the Seller may terminate the Agreement before the effective date of the changes.</p>
        <p>The Seller's continued use of the Platform after the effective date constitutes binding acceptance of the amended Terms, pricing, and commercial structure.</p>

        <h3>Legal Framework</h3>
        <p>SDeal acts as a commercial agent as defined in Articles 7:428 et seq. of the Dutch Civil Code.</p>
        <p>SDeal promotes and facilitates the sale of the Seller's products to consumers through the Platform but is not a contracting party to any consumer transaction.</p>
        <p>All sales agreements are legally concluded between the Seller and the consumer. SDeal merely acts as an intermediary.</p>
        <p>Nothing in these Terms shall be construed as establishing an employment, partnership, or joint venture relationship between the Parties.</p>

        <h3>Platform Access and SaaS Fee</h3>
        <p>Access to the Platform and related tools is provided as a Software-as-a-Service (SaaS) subscription.</p>
        <p>Sellers select a service package (A, B, or C) as specified on www.sdeal.com/pricing.</p>
        <p>The monthly SaaS Fee is invoiced in advance and payable within 14 days. Non-payment may result in suspension of access to the Platform.</p>
        <p>SDeal may update the technical functionality of the Platform or its packages at any time to improve performance or security.</p>

        <h3>Commission and Marketing</h3>
        <p>The Seller determines the commission percentage applicable to each sale, subject to the package minimums: Starter from 15%, Growth and Pro from 8% (as further specified in the Agreement and pricing).</p>
        <p>SDeal allocates the agreed portion of the commission toward marketing and promotion activities, including but not limited to Google Shopping and social media channels.</p>
        <p>Commissions are automatically deducted from Seller payouts.</p>

        <h3>Transaction Fees</h3>
        <p>For each completed order processed through the Platform, SDeal charges a transaction fee as stated in the Seller's pricing overview.</p>
        <p>Transaction fees cover payment processing, order reconciliation, customer communication, and administrative handling.</p>
        <p>These fees are automatically deducted from the Seller's payout together with the applicable commission.</p>

        <h3>Payments and Client Funds</h3>
        <p>All payments from consumers are processed via the independent Stichting Cliëntgelden SDeal or a licensed Payment Service Provider (PSP).</p>
        <p>SDeal does not at any time hold or manage client funds.</p>
        <p>Funds are released to Sellers after the applicable delivery and return period. During this interim period, the funds remain safeguarded by the Stichting and cannot be accessed or used by SDeal B.V.</p>
        <p>The €250 Reserve is a contractual deduction serving as security for refunds or chargebacks and does not accrue interest.</p>
        <p>SDeal, the Stichting, and the PSP are not responsible for payment delays resulting from incomplete or incorrect Seller data.</p>

        <h3>Seller Obligations</h3>
        <p>The Seller is responsible for:</p>
        <ul>
          <li>supplying accurate and up-to-date product information, pricing, and stock data,</li>
          <li>complying with all applicable consumer, tax, and product safety laws,</li>
          <li>ensuring timely shipment, delivery, and handling of returns or warranty claims, and</li>
          <li>responding to consumer inquiries in a timely and professional manner.</li>
        </ul>
        <p>The Seller indemnifies SDeal against any claims arising from product defects, delivery failures, or violations of consumer law.</p>

        <h3>Use of the Platform</h3>
        <p>Sellers shall not upload or sell products that are illegal, counterfeit, or infringe intellectual property rights.</p>
        <p>SDeal reserves the right to remove listings, suspend accounts, or terminate cooperation if products or conduct violate these Terms or applicable law.</p>
        <p>The Seller may not use the Platform to divert customers away from SDeal through misleading links or unauthorized promotions.</p>

        <h3>Data and Privacy</h3>
        <p>Each Party shall comply with the General Data Protection Regulation (GDPR).</p>
        <p>SDeal processes personal data as a data controller for Platform operations and as a data processor for transactions concluded by the Seller.</p>
        <p>The Seller may access customer data solely for order fulfillment and must protect such data against unauthorized access or use.</p>

        <h3>Liability and Indemnification</h3>
        <p>SDeal's total liability to the Seller for any claim shall not exceed the total amount of SaaS fees and commissions paid by the Seller in the three months preceding the claim.</p>
        <p>SDeal shall not be liable for indirect or consequential damages, loss of profits, or business interruption.</p>
        <p>The Seller shall indemnify and hold SDeal harmless from any third-party claims arising from product liability, misrepresentation, or breach of these Terms.</p>

        <h3>Termination</h3>
        <p>This Agreement has a minimum term of six (6) months from the start date.</p>
        <p>After the first six (6) months, either Party may terminate the Agreement on a monthly basis by giving notice during a calendar month. Upon such notice, the Agreement ends and the Seller’s account becomes inactive at the end of that same calendar month.</p>
        <p>Upon termination, SDeal will refund the €250 Reserve within 30 days, provided there are no outstanding obligations or open claims.</p>
        <p>Any unpaid invoices, fees, or commissions remain due and payable after termination.</p>

        <h3>Confidentiality</h3>
        <p>Both Parties shall treat as confidential all non-public information obtained in connection with this Agreement.</p>
        <p>This obligation survives termination of the Agreement.</p>

        <h3>Applicable Law and Jurisdiction</h3>
        <p>These Terms and all related agreements are governed by Dutch law.</p>
        <p>Any disputes shall be submitted exclusively to the District Court of Groningen, the Netherlands.</p>

        <h3>Contact</h3>
        <p>SDeal B.V.<br />
        Osloweg 110, 9723 BX Groningen, The Netherlands<br />
        KVK: 76103080<br />
        VAT: NL 860508468B01<br />
        Email: <a href="mailto:info@sdeal.com">info@sdeal.com</a><br />
        Website: <a href="https://www.sdeal.com/terms-sellers">www.sdeal.com/terms-sellers</a></p>
      </div>
    </div>
  );
};

export default TermsSellers;

